/**
 * Metal Detector tab (CCP 03) — Daily Kitchen Record.
 *
 * Workflow (matches SOP JIFP-WI-03 F02):
 *   1. Operator opens the record with an OPENING sample test on
 *      Fe / Non-Fe / SS. All three MUST pass before production may
 *      begin. Failed attempts are logged (retry until pass).
 *   2. Operator enters production line items (batch, item, qty).
 *      Any line flagged NC (contamination caught) MUST include the
 *      action taken. Optional photo evidence per NC line.
 *   3. Operator runs a CLOSING sample test at end of batch. All three
 *      MUST pass. If any fail, operator can retry OR escalate to
 *      QA/QC (whole batch flagged for review).
 *   4. Supervisor verifies (immutable) or rejects back with a reason.
 *
 * Mirrors ThawingTab's visual grammar so operators feel at home.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { useDialog } from "@/context/DialogProvider";
import api, { formatErr } from "@/lib/api";
import { Portal } from "@/components/Portal";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChevronRight, Plus, RefreshCw,
  ShieldAlert, Trash2, X, Camera, Radar, ImageIcon, Lock,
} from "lucide-react";

const STATUS_LABEL = {
  production_open: "Production open",
  opening_failed: "Opening test failed",
  closing_failed: "Closing test failed",
  pending_verify: "Awaiting verification",
  verified: "Verified",
  rejected: "Rejected",
};

const dtLocalNow = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const toISO = (v) => (v ? new Date(v).toISOString() : null);
const fmt = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString(undefined,
      { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return v; }
};

function StatusPill({ status }) {
  const cls = status === "verified" ? "ok"
    : status === "pending_verify" ? "warn"
    : status === "opening_failed" || status === "closing_failed" || status === "rejected" ? "err"
    : "";
  return (
    <span className={`vb-pill ${cls} text-[10px]`}
      data-testid={`md-status-${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}
function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-xs uppercase font-semibold opacity-70">{label}</label>
      {children}
      {hint && <p className="text-[11px] opacity-60 mt-0.5">{hint}</p>}
    </div>
  );
}

function useKitchenFacilities() {
  const { activeSiteId } = useSite();
  const [facilities, setFacilities] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/metal-detector/facilities")
      .then(({ data }) => { if (!cancelled) setFacilities(data.facilities || []); })
      .catch(() => { if (!cancelled) setFacilities([]); });
    return () => { cancelled = true; };
  }, [activeSiteId]);
  return facilities;
}
function useSiteUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/hot-cold-hold/site-users")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => setUsers([]));
  }, []);
  return users;
}

/* ===================== small shared bits ===================== */
function SampleTestTriple({ value, onChange, disabled }) {
  const rows = [
    { key: "fe_pass", label: "Fe · 2.5 mm" },
    { key: "non_fe_pass", label: "Non-Fe · 2.5 mm" },
    { key: "ss_pass", label: "SS · 3.5 mm" },
  ];
  return (
    <div className="space-y-2" data-testid="md-sample-triple">
      {rows.map((r) => {
        const v = value[r.key];
        return (
          <div key={r.key} className="flex items-center justify-between gap-3 p-2 rounded-lg"
            style={{ background: "var(--vb-elev)", border: "1px solid var(--vb-border)" }}>
            <span className="text-sm font-medium">{r.label}</span>
            <div className="flex gap-2">
              <button type="button" disabled={disabled}
                className={`vb-pill ${v === true ? "ok" : ""}`}
                onClick={() => onChange({ ...value, [r.key]: true })}
                data-testid={`md-sample-${r.key}-pass`}>
                <CheckCircle2 size={12} /> Detected
              </button>
              <button type="button" disabled={disabled}
                className={`vb-pill ${v === false ? "err" : ""}`}
                onClick={() => onChange({ ...value, [r.key]: false })}
                data-testid={`md-sample-${r.key}-fail`}>
                <AlertTriangle size={12} /> Fail
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
function allTriplePass(v) {
  return v.fe_pass === true && v.non_fe_pass === true && v.ss_pass === true;
}
function anyTripleChosen(v) {
  return v.fe_pass !== null && v.non_fe_pass !== null && v.ss_pass !== null;
}

/* ===================== main tab ===================== */
export default function MetalDetectorTab({ canVerify, needsSite, noFacility }) {
  const { activeSiteId } = useSite();
  const { confirm } = useDialog();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/metal-detector/records?status=${statusFilter}`);
    setRows(data.records || []);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load, activeSiteId]);

  const del = async (r) => {
    if (!await confirm({
      title: "Delete this metal detector record?",
      message: "Verified records cannot be deleted.",
      danger: true, confirmLabel: "Delete",
    })) return;
    try {
      await api.delete(`/metal-detector/records/${r.id}`);
      toast.success("Deleted"); setSelected(null); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    }
  };
  const verify = async (r, notes = "") => {
    try {
      await api.put(`/metal-detector/records/${r.id}/verify`, { notes });
      toast.success("Verified & locked");
      setSelected(null); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Verify failed");
    }
  };
  const reject = async (r, reason) => {
    try {
      await api.put(`/metal-detector/records/${r.id}/reject`, { reason });
      toast.success("Rejected & locked");
      setSelected(null); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Reject failed");
    }
  };
  const refresh = async (r) => {
    const { data } = await api.get(`/metal-detector/records/${r.id}`);
    setSelected(data.record);
    load();
  };

  const options = ["all", "production_open", "opening_failed",
                   "closing_failed", "pending_verify", "verified", "rejected"];

  return (
    <div data-testid="metal-detector-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs opacity-70">
          <span className="vb-pill" style={{ background: "rgba(220,38,38,0.08)",
            border: "1px solid rgba(220,38,38,0.3)", color: "#b91c1c" }}>
            <Radar size={12} /> CCP 03
          </span>
          <span className="ml-2">Critical limits: Fe 2.5 mm · Non-Fe 2.5 mm · SS 3.5 mm</span>
        </div>
        <button className="vb-btn" onClick={() => setShowNew(true)}
          disabled={needsSite || noFacility} data-testid="new-md"
          title={noFacility ? "Add a food-production facility to this site first." : undefined}>
          <Plus size={16} /> New record
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {options.map((s) => (
          <button key={s} className={`vb-pill ${statusFilter === s ? "ok" : ""}`}
            style={{ cursor: "pointer" }} onClick={() => setStatusFilter(s)}
            data-testid={`md-filter-${s}`}>
            {s === "all" ? "All" : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="vb-card overflow-x-auto">
        <table className="vb-table">
          <thead>
            <tr>
              <th>Date · <span className="opacity-60 font-normal">日期</span></th>
              <th>Detector · <span className="opacity-60 font-normal">检测器</span></th>
              <th>Opening test</th>
              <th>Batches · <span className="opacity-60 font-normal">批次</span></th>
              <th>NC</th>
              <th>Closing test</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 28 }}
                  className="opacity-60">
                  No metal-detector records yet. Click <em>New record</em> to log one.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const opening = r.opening_test || {};
              const closing = r.closing_test || {};
              const lines = r.production_lines || [];
              const ncCount = lines.filter((l) => l.nc_detected).length;
              const batches = [...new Set(lines.map((l) => l.batch_no).filter(Boolean))].join(", ");
              return (
                <tr key={r.id} data-testid={`md-row-${r.id}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelected(r)}>
                  <td className="opacity-70">{(r.created_at || "").slice(0, 10)}</td>
                  <td><strong>{r.detector_id || "—"}</strong>
                    <div className="opacity-60 text-[11px]">{r.facility_name || ""}</div>
                  </td>
                  <td>
                    {opening.passed
                      ? <span className="vb-pill ok text-[10px]">PASS</span>
                      : <span className="vb-pill err text-[10px]">FAIL</span>}
                    <span className="opacity-60 text-[11px] ml-1">
                      ({(opening.attempts || []).length}×)
                    </span>
                  </td>
                  <td className="text-xs opacity-80">{batches || "—"}</td>
                  <td>
                    {ncCount > 0
                      ? <span className="vb-pill err text-[10px]">{ncCount} NC</span>
                      : <span className="opacity-40 text-[10px]">0</span>}
                  </td>
                  <td>
                    {!r.closing_test ? <span className="opacity-40 text-[10px]">—</span>
                      : closing.passed
                        ? <span className="vb-pill ok text-[10px]">PASS</span>
                        : <span className="vb-pill err text-[10px]">FAIL</span>}
                    {r.batch_qa_review_required && (
                      <span className="ml-1 vb-pill err text-[10px]" title="Escalated to QA">
                        <ShieldAlert size={10} /> QA
                      </span>
                    )}
                  </td>
                  <td><StatusPill status={r.status} /></td>
                  <td className="text-right">
                    <ChevronRight size={14} className="opacity-50" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewMDModal
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }} />
      )}
      {selected && (
        <MDDetailDrawer record={selected} canVerify={canVerify}
          onClose={() => setSelected(null)}
          onVerify={(notes) => verify(selected, notes)}
          onReject={(reason) => reject(selected, reason)}
          onDelete={() => del(selected)}
          onRefresh={() => refresh(selected)} />
      )}
    </div>
  );
}

/* ===================== new-record modal ===================== */
function NewMDModal({ onClose, onSaved }) {
  const { user: me } = useAuth();
  const users = useSiteUsers();
  const facilities = useKitchenFacilities();
  const [facilityId, setFacilityId] = useState("");
  const [detectorId, setDetectorId] = useState("");
  const [testedAt, setTestedAt] = useState(dtLocalNow());
  const [recBy, setRecBy] = useState({ id: me?.id || "", name: me?.name || "" });
  const [notes, setNotes] = useState("");
  const [triple, setTriple] = useState({ fe_pass: null, non_fe_pass: null, ss_pass: null });
  const [testNote, setTestNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!facilityId && facilities.length === 1) setFacilityId(facilities[0].id);
  }, [facilities, facilityId]);

  const anyFail = anyTripleChosen(triple) && !allTriplePass(triple);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (!anyTripleChosen(triple)) {
        throw new Error("Record every sample tester result (Fe / Non-Fe / SS).");
      }
      await api.post("/metal-detector/records", {
        facility_id: facilityId,
        detector_id: detectorId,
        recorded_by: recBy.name,
        recorded_by_id: recBy.id,
        notes,
        opening_test: {
          fe_pass: !!triple.fe_pass,
          non_fe_pass: !!triple.non_fe_pass,
          ss_pass: !!triple.ss_pass,
          note: testNote,
          tested_at: toISO(testedAt),
        },
      });
      toast.success(allTriplePass(triple)
        ? "Record started — production may begin"
        : "Opening test failed — machine needs inspection & retest");
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        <form className="vb-modal p-5 w-full max-w-2xl"
          data-testid="new-md-modal"
          onClick={(e) => e.stopPropagation()} onSubmit={submit}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="vb-heading text-xl">New Metal Detector Record</h3>
              <p className="text-xs opacity-70 mt-0.5">
                Run the sample tester on all three types first. All three MUST
                detect (Fe / Non-Fe / SS) before production may begin.
              </p>
            </div>
            <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Kitchen (facility)"
              hint={facilities.length === 0
                ? "No food-production facility set up for this site."
                : "Only food-production facilities are shown."}>
              <select className="vb-input" required value={facilityId}
                onChange={(e) => setFacilityId(e.target.value)}
                data-testid="md-facility">
                <option value="" disabled>Pick a kitchen</option>
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Detector ID / Location">
              <input className="vb-input" placeholder="e.g. MD-01 (Line 2)"
                value={detectorId} onChange={(e) => setDetectorId(e.target.value)}
                data-testid="md-detector-id" />
            </Field>
            <Field label="Tested at" hint="When you ran the sample tester.">
              <input className="vb-input" type="datetime-local" required
                value={testedAt} onChange={(e) => setTestedAt(e.target.value)}
                data-testid="md-tested-at" />
            </Field>
            <Field label="Recorded by">
              <select className="vb-input"
                value={recBy.id}
                onChange={(e) => {
                  const u = users.find((x) => x.id === e.target.value);
                  setRecBy({ id: u?.id || "", name: u?.name || "" });
                }}
                data-testid="md-recorded-by">
                {users.length === 0 && (
                  <option value={me?.id}>{me?.name || me?.email}</option>
                )}
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="mt-4">
            <div className="text-xs uppercase font-semibold opacity-70 mb-2">
              Opening sample tester result
            </div>
            <SampleTestTriple value={triple} onChange={setTriple} />
            {anyFail && (
              <div className="mt-3">
                <Field label="Failure note / inspection action taken"
                  hint="Recorded permanently in the audit trail.">
                  <textarea className="vb-input" rows={2}
                    value={testNote}
                    onChange={(e) => setTestNote(e.target.value)}
                    data-testid="md-test-note" />
                </Field>
              </div>
            )}
          </div>

          <div className="mt-3">
            <Field label="Notes (optional)">
              <textarea className="vb-input" rows={2} value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="md-notes" />
            </Field>
          </div>

          {err && <div className="vb-pill err mt-3" data-testid="md-form-error">{err}</div>}

          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy}
              data-testid="md-submit">
              {busy ? "Saving…" : "Save opening test"}
            </button>
          </div>
        </form>
      </div>
    </Portal>
  );
}

/* ===================== detail drawer ===================== */
function MDDetailDrawer({ record: initial, canVerify, onClose, onVerify, onReject, onDelete, onRefresh }) {
  const [record, setRecord] = useState(initial);
  useEffect(() => { setRecord(initial); }, [initial]);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalateReason, setEscalateReason] = useState("");

  const status = record.status;
  const opening = record.opening_test || {};
  const closing = record.closing_test || {};
  const lines = record.production_lines || [];

  const canWrite = status !== "verified" && status !== "rejected";
  const canEditLines = status === "production_open" || status === "closing_failed";
  const canRetryOpening = status === "opening_failed";
  const canRunClosing = status === "production_open" && lines.length > 0;
  const canRetryClosing = status === "closing_failed";
  const canDelete = canVerify && status !== "verified" && status !== "rejected";
  const canAct = canVerify && status === "pending_verify";
  const isLocked = status === "verified" || status === "rejected";

  const reloadRecord = async () => {
    const { data } = await api.get(`/metal-detector/records/${record.id}`);
    setRecord(data.record);
    onRefresh?.();
  };

  const confirmNCLine = async (line_id) => {
    try {
      await api.put(`/metal-detector/records/${record.id}/lines/${line_id}/confirm-nc`,
        { notes: "" });
      toast.success("NC confirmed — locked into audit trail");
      reloadRecord();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Confirmation failed");
    }
  };

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        <div className="vb-modal p-5 w-full max-w-5xl md:max-w-6xl"
          style={{ maxHeight: "92vh", overflowY: "auto" }}
          data-testid="md-detail-drawer"
          onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="vb-heading text-xl">
                {record.detector_id || "Metal Detector"}
              </h3>
              <p className="text-xs opacity-70 mt-0.5">
                {record.facility_name || "—"} · Created {fmt(record.created_at)}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <StatusPill status={status} />
                {record.batch_qa_review_required && (
                  <span className="vb-pill err text-[10px]">
                    <ShieldAlert size={11} /> QA/QC review required
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="opacity-60 hover:opacity-100">
              <X size={18} />
            </button>
          </div>

          {record.rejection && (
            <div className="mb-3 p-3 rounded-lg" style={{
              background: "rgba(220,38,38,0.07)",
              border: "1px solid rgba(220,38,38,0.3)",
            }} data-testid="md-rejection-banner">
              <div className="text-xs uppercase font-semibold mb-1 flex items-center gap-1"
                style={{ color: "#b91c1c" }}>
                <Lock size={12} /> Rejected &amp; locked
              </div>
              <div className="text-sm">
                {record.rejection.rejected_by_name} · {fmt(record.rejection.rejected_at)}
                <div className="opacity-80 mt-1">{record.rejection.reason}</div>
              </div>
            </div>
          )}

          {/* Opening test panel */}
          <div className="mt-1 p-3 rounded-lg"
            style={{ background: "var(--vb-elev)", border: "1px solid var(--vb-border)" }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase font-semibold opacity-70">
                Opening sample tester
              </div>
              <span className={`vb-pill ${opening.passed ? "ok" : "err"} text-[10px]`}>
                {opening.passed ? "PASSED" : "FAILING"}
                {opening.passed_at && <> · {fmt(opening.passed_at)}</>}
              </span>
            </div>
            <AttemptList attempts={opening.attempts} />
            {canRetryOpening && (
              <RetryTestForm phase="opening" recordId={record.id}
                onSaved={reloadRecord} />
            )}
          </div>

          {/* Production lines */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase font-semibold opacity-70">
                Production line items ({lines.length})
              </div>
            </div>
            {canEditLines
              ? <ProductionLinesEditor recordId={record.id} lines={lines}
                  canVerify={canVerify} onConfirmNC={confirmNCLine}
                  onSaved={reloadRecord} />
              : <ProductionLinesReadOnly lines={lines}
                  canVerify={canVerify}
                  recordLocked={isLocked}
                  onConfirmNC={confirmNCLine} />}
          </div>

          {/* Closing test panel */}
          {(closing.attempts?.length || canRunClosing || canRetryClosing) && (
            <div className="mt-4 p-3 rounded-lg"
              style={{ background: "var(--vb-elev)", border: "1px solid var(--vb-border)" }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase font-semibold opacity-70">
                  Closing sample tester
                </div>
                {closing.attempts?.length ? (
                  <span className={`vb-pill ${closing.passed ? "ok" : "err"} text-[10px]`}>
                    {closing.passed ? "PASSED" : "FAILING"}
                  </span>
                ) : null}
              </div>
              <AttemptList attempts={closing.attempts} />
              {(canRunClosing || canRetryClosing) && (
                <RetryTestForm phase="closing" recordId={record.id}
                  onSaved={reloadRecord} />
              )}
              {status === "closing_failed" && (
                <div className="mt-3 p-3 rounded-lg" style={{
                  background: "rgba(220,38,38,0.07)",
                  border: "1px solid rgba(220,38,38,0.3)",
                }} data-testid="md-escalate-box">
                  <div className="text-xs font-semibold mb-1"
                    style={{ color: "#b91c1c" }}>
                    Closing test failed — batch requires QA/QC inspection
                  </div>
                  <p className="text-[11px] opacity-70 mb-2">
                    Retry with a clean/inspected machine above, OR escalate the
                    entire batch to QA/QC and send it up for supervisor review.
                  </p>
                  {!showEscalate ? (
                    <button className="vb-btn danger text-xs"
                      onClick={() => setShowEscalate(true)}
                      data-testid="md-escalate-open">
                      <ShieldAlert size={13} /> Escalate to QA/QC
                    </button>
                  ) : (
                    <div>
                      <textarea className="vb-input" rows={2}
                        placeholder="Who did you notify and why?"
                        value={escalateReason}
                        onChange={(e) => setEscalateReason(e.target.value)}
                        data-testid="md-escalate-reason" />
                      <div className="flex justify-end gap-2 mt-2">
                        <button className="vb-btn ghost text-xs"
                          onClick={() => setShowEscalate(false)}>Cancel</button>
                        <button className="vb-btn danger text-xs"
                          disabled={!escalateReason.trim()}
                          onClick={async () => {
                            try {
                              await api.post(`/metal-detector/records/${record.id}/mark-qa-review`,
                                { reason: escalateReason.trim() });
                              toast.success("Escalated — sent for supervisor review");
                              setShowEscalate(false);
                              setEscalateReason("");
                              reloadRecord();
                            } catch (e) {
                              toast.error(formatErr(e.response?.data?.detail) || "Escalation failed");
                            }
                          }}
                          data-testid="md-escalate-confirm">
                          Confirm escalation
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* QA escalation banner (visible to supervisor) */}
          {record.qa_escalation && (
            <div className="mt-3 p-3 rounded-lg" style={{
              background: "rgba(220,38,38,0.07)",
              border: "1px solid rgba(220,38,38,0.3)",
            }}>
              <div className="text-xs uppercase font-semibold mb-1"
                style={{ color: "#b91c1c" }}>QA escalation</div>
              <div className="text-sm">
                {record.qa_escalation.notified_by} · {fmt(record.qa_escalation.notified_at)}
                <div className="opacity-70 mt-1">{record.qa_escalation.reason}</div>
              </div>
            </div>
          )}

          {record.verification && (
            <div className="mt-3 p-3 rounded-lg" style={{
              background: "rgba(34,197,94,0.07)",
              border: "1px solid rgba(34,197,94,0.3)",
            }}>
              <div className="text-xs uppercase font-semibold mb-1"
                style={{ color: "#15803d" }}>Verified</div>
              <div className="text-sm">
                {record.verification.verified_by_name} · {fmt(record.verification.verified_at)}
                {record.verification.notes && (
                  <div className="opacity-70 mt-1">{record.verification.notes}</div>
                )}
              </div>
            </div>
          )}

          {showReject && (
            <div className="mt-3 p-3 rounded-lg" style={{
              background: "rgba(220,38,38,0.07)",
              border: "1px solid rgba(220,38,38,0.3)",
            }}>
              <div className="text-xs uppercase font-semibold mb-1 flex items-center gap-1"
                style={{ color: "#b91c1c" }}>
                <Lock size={12} /> Reject &amp; lock this record
              </div>
              <p className="text-[11px] opacity-80 mb-2">
                This is a <strong>terminal action</strong>. The record and all
                its line items will be permanently locked as a rejected audit
                entry (batch discarded). If the operator made a mistake, they
                must create a fresh record.
              </p>
              <textarea className="vb-input" rows={2}
                placeholder="Reason (e.g. Closing test failed — batch discarded)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                data-testid="md-reject-reason" />
              <div className="flex justify-end gap-2 mt-2">
                <button className="vb-btn ghost text-xs" onClick={() => setShowReject(false)}>
                  Cancel
                </button>
                <button className="vb-btn danger text-xs"
                  disabled={!rejectReason.trim()}
                  onClick={() => { onReject(rejectReason.trim()); setShowReject(false); }}
                  data-testid="md-reject-confirm">
                  <Lock size={12} /> Reject &amp; lock
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4 flex-wrap">
            <button className="vb-btn ghost text-xs" onClick={reloadRecord}
              data-testid="md-refresh">
              <RefreshCw size={13} /> Refresh
            </button>
            {canDelete && (
              <button className="vb-btn ghost text-xs" onClick={onDelete}
                data-testid="md-delete">
                <Trash2 size={13} /> Delete
              </button>
            )}
            {canAct && !showReject && (
              <button className="vb-btn danger text-xs"
                onClick={() => setShowReject(true)}
                data-testid="md-open-reject">
                <AlertTriangle size={13} /> Reject &amp; lock
              </button>
            )}
            {canAct && (
              <button className="vb-btn text-xs" onClick={() => onVerify("")}
                data-testid="md-verify">
                <CheckCircle2 size={13} /> Verify &amp; lock
              </button>
            )}
            <button className="vb-btn secondary text-xs" onClick={onClose}>
              Close <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

/* ===================== attempt list ===================== */
function AttemptList({ attempts }) {
  const rows = attempts || [];
  if (!rows.length) return <div className="text-xs opacity-60">No attempts yet.</div>;
  return (
    <div className="space-y-1" data-testid="md-attempt-list">
      {rows.map((a, i) => (
        <div key={a.id || i} className="text-xs flex items-center gap-2 flex-wrap"
          data-testid={`md-attempt-${i}`}>
          <span className="opacity-60 w-6">#{i + 1}</span>
          <span>{fmt(a.tested_at)}</span>
          <span>·</span>
          <span>{a.tested_by || "—"}</span>
          <span>·</span>
          <span className={`vb-pill ${a.fe_pass ? "ok" : "err"} text-[10px]`}>Fe {a.fe_pass ? "✓" : "✗"}</span>
          <span className={`vb-pill ${a.non_fe_pass ? "ok" : "err"} text-[10px]`}>Non-Fe {a.non_fe_pass ? "✓" : "✗"}</span>
          <span className={`vb-pill ${a.ss_pass ? "ok" : "err"} text-[10px]`}>SS {a.ss_pass ? "✓" : "✗"}</span>
          {a.note && <span className="opacity-70 italic">— {a.note}</span>}
        </div>
      ))}
    </div>
  );
}

/* ===================== retry / closing test form ===================== */
function RetryTestForm({ phase, recordId, onSaved }) {
  const [triple, setTriple] = useState({ fe_pass: null, non_fe_pass: null, ss_pass: null });
  const [note, setNote] = useState("");
  const [testedAt, setTestedAt] = useState(dtLocalNow());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      if (!anyTripleChosen(triple)) {
        throw new Error("Record every sample tester result (Fe / Non-Fe / SS).");
      }
      await api.post(`/metal-detector/records/${recordId}/sample-test`, {
        phase,
        fe_pass: !!triple.fe_pass,
        non_fe_pass: !!triple.non_fe_pass,
        ss_pass: !!triple.ss_pass,
        note,
        tested_at: toISO(testedAt),
      });
      toast.success(allTriplePass(triple)
        ? (phase === "opening" ? "Opening passed — you may begin production" : "Closing passed — sent for verification")
        : "Failure logged — please retry after inspection");
      setTriple({ fe_pass: null, non_fe_pass: null, ss_pass: null });
      setNote("");
      onSaved?.();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 p-3 rounded-lg" style={{
      background: "var(--vb-bg)", border: "1px dashed var(--vb-border)",
    }} data-testid={`md-retry-${phase}`}>
      <div className="text-xs font-semibold mb-2">
        {phase === "opening" ? "Retry opening sample test" : "Run closing sample test"}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <Field label="Tested at">
          <input className="vb-input" type="datetime-local"
            value={testedAt} onChange={(e) => setTestedAt(e.target.value)}
            data-testid={`md-${phase}-tested-at`} />
        </Field>
        <Field label="Note (optional)">
          <input className="vb-input" value={note}
            placeholder={phase === "opening" ? "e.g. Cleaned sensor & recalibrated" : "e.g. End of batch"}
            onChange={(e) => setNote(e.target.value)}
            data-testid={`md-${phase}-note`} />
        </Field>
      </div>
      <SampleTestTriple value={triple} onChange={setTriple} disabled={busy} />
      {err && <div className="vb-pill err mt-2 text-[11px]">{err}</div>}
      <div className="flex justify-end mt-2">
        <button className="vb-btn text-xs" onClick={submit} disabled={busy}
          data-testid={`md-${phase}-submit`}>
          {busy ? "Saving…" : phase === "opening" ? "Save opening test" : "Save closing test"}
        </button>
      </div>
    </div>
  );
}

/* ===================== production lines editor ===================== */
function ProductionLinesEditor({ recordId, lines: initialLines, canVerify, onConfirmNC, onSaved }) {
  const [lines, setLines] = useState(() =>
    (initialLines || []).map((l) => ({ ...l })),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLines((initialLines || []).map((l) => ({ ...l })));
  }, [initialLines]);

  const setField = (i, key, val) =>
    setLines((xs) => xs.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
  const addLine = () =>
    setLines((xs) => [...xs, {
      id: undefined, batch_no: "", item: "", quantity: "",
      nc_detected: false, action_taken: "", photos: [],
    }]);
  const removeLine = (i) =>
    setLines((xs) => xs.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const clean = lines
        .map((l) => ({
          id: l.id,
          batch_no: (l.batch_no || "").trim(),
          item: (l.item || "").trim(),
          quantity: (l.quantity || "").trim(),
          nc_detected: !!l.nc_detected,
          action_taken: (l.action_taken || "").trim(),
        }))
        .filter((l) => l.batch_no && l.item && l.quantity);
      await api.put(`/metal-detector/records/${recordId}/production-lines`,
        { lines: clean });
      toast.success("Production lines saved");
      onSaved?.();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const uploadPhoto = async (lineId, file) => {
    if (!lineId) {
      toast.error("Save the line first before adding a photo.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/metal-detector/records/${recordId}/lines/${lineId}/photo`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      onSaved?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Photo upload failed");
    }
  };

  return (
    <div className="vb-card p-3" data-testid="md-lines-editor">
      {/* Desktop / tablet: table view (>= md breakpoint) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="vb-table text-sm" style={{ minWidth: 640 }}>
          <thead>
            <tr>
              <th style={{ width: 140 }}>Batch No.</th>
              <th style={{ minWidth: 200 }}>Item · 产品</th>
              <th style={{ width: 120 }}>Qty</th>
              <th style={{ width: 150 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={4} className="opacity-60 text-center py-4">
                No line items yet — click <em>Add line</em>.
              </td></tr>
            )}
            {lines.map((l, i) => {
              const isLocked = !!(l.id && l.nc_detected);
              const ncConfirmed = l.nc_status === "confirmed";
              const ncPending = isLocked && !ncConfirmed;
              return (
              <Fragment key={l.id || `new-${i}`}>
                <tr data-testid={`md-line-row-${i}`}
                    style={{ borderBottom: l.nc_detected ? "none" : undefined,
                             background: isLocked
                               ? (ncConfirmed ? "rgba(220,38,38,0.05)" : "rgba(234,88,12,0.05)")
                               : undefined }}>
                  <td>
                    <input className="vb-input text-sm" value={l.batch_no}
                      disabled={isLocked}
                      onChange={(e) => setField(i, "batch_no", e.target.value)}
                      data-testid={`md-line-batch-${i}`} />
                  </td>
                  <td>
                    <input className="vb-input text-sm" value={l.item}
                      disabled={isLocked}
                      onChange={(e) => setField(i, "item", e.target.value)}
                      data-testid={`md-line-item-${i}`} />
                  </td>
                  <td>
                    <input className="vb-input text-sm" value={l.quantity}
                      disabled={isLocked}
                      placeholder="e.g. 50 pcs"
                      onChange={(e) => setField(i, "quantity", e.target.value)}
                      data-testid={`md-line-qty-${i}`} />
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {ncConfirmed ? (
                        <span className="vb-pill err text-[10px]"
                          title={`Confirmed by ${l.nc_confirmation?.confirmed_by_name || "supervisor"} on ${fmt(l.nc_confirmation?.confirmed_at)}`}
                          data-testid={`md-line-locked-${i}`}>
                          <Lock size={10} /> NC confirmed
                        </span>
                      ) : ncPending ? (
                        <span className="vb-pill warn text-[10px]"
                          title="NC awaiting supervisor confirmation"
                          data-testid={`md-line-pending-${i}`}>
                          <AlertTriangle size={10} /> NC · pending confirm
                        </span>
                      ) : (
                        <label className="inline-flex items-center gap-1 text-xs"
                          title="Mark this line as non-conforming (metal detected)">
                          <input type="checkbox" checked={!!l.nc_detected}
                            onChange={(e) => setField(i, "nc_detected", e.target.checked)}
                            data-testid={`md-line-nc-${i}`} />
                          NC
                        </label>
                      )}
                      {ncPending && canVerify && (
                        <button className="vb-btn danger" type="button"
                          style={{ padding: "2px 6px", fontSize: "10px" }}
                          onClick={() => onConfirmNC?.(l.id)}
                          title="Supervisor: confirm this rejection & lock in audit trail"
                          data-testid={`md-line-confirm-nc-${i}`}>
                          <CheckCircle2 size={11} /> Confirm rejection
                        </button>
                      )}
                      {(l.photos || []).map((p, pi) => (
                        <a key={pi} href={p.url} target="_blank" rel="noreferrer"
                          title={fmt(p.uploaded_at)}
                          className="opacity-70 hover:opacity-100">
                          <ImageIcon size={14} />
                        </a>
                      ))}
                      <label className="vb-btn ghost cursor-pointer"
                        style={{ padding: "4px 6px" }}
                        title={l.id ? "Add photo" : "Save first"}>
                        <Camera size={13} />
                        <input type="file" accept="image/*" hidden
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadPhoto(l.id, f);
                            e.target.value = "";
                          }}
                          data-testid={`md-line-photo-${i}`} />
                      </label>
                      {!isLocked && (
                        <button className="vb-btn ghost" onClick={() => removeLine(i)}
                          style={{ padding: "4px 6px" }}
                          title="Remove line"
                          data-testid={`md-line-remove-${i}`}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {l.nc_detected && (
                  <tr data-testid={`md-line-nc-row-${i}`}>
                    <td colSpan={4} style={{ paddingTop: 0 }}>
                      <div className="p-2 rounded-lg" style={{
                        background: ncConfirmed
                          ? "rgba(220,38,38,0.06)"
                          : "rgba(234,88,12,0.06)",
                        border: `1px solid ${ncConfirmed
                          ? "rgba(220,38,38,0.25)"
                          : "rgba(234,88,12,0.3)"}`,
                      }}>
                        <label className="text-[11px] uppercase font-semibold flex items-center gap-1 mb-1"
                          style={{ color: ncConfirmed ? "#b91c1c" : "#c2410c" }}>
                          <AlertTriangle size={11} /> Action taken (required)
                          {ncConfirmed && (
                            <span className="ml-auto text-[10px] opacity-70 inline-flex items-center gap-1">
                              <Lock size={10} /> Confirmed by {l.nc_confirmation?.confirmed_by_name || "supervisor"}
                            </span>
                          )}
                          {ncPending && (
                            <span className="ml-auto text-[10px] opacity-70">
                              Awaiting supervisor confirmation
                            </span>
                          )}
                        </label>
                        <textarea className="vb-input text-sm" rows={2}
                          value={l.action_taken}
                          disabled={isLocked}
                          placeholder="e.g. Rechecked → still positive → rejected → notified QA"
                          onChange={(e) => setField(i, "action_taken", e.target.value)}
                          data-testid={`md-line-action-${i}`} />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked card view (< md breakpoint) */}
      <div className="md:hidden space-y-3">
        {lines.length === 0 && (
          <div className="opacity-60 text-center py-4 text-sm">
            No line items yet — tap <em>Add line</em>.
          </div>
        )}
        {lines.map((l, i) => {
          const isLocked = !!(l.id && l.nc_detected);
          const ncConfirmed = l.nc_status === "confirmed";
          const ncPending = isLocked && !ncConfirmed;
          const cardBg = ncConfirmed ? "rgba(220,38,38,0.06)"
            : ncPending ? "rgba(234,88,12,0.06)"
            : "var(--vb-elev)";
          const cardBorder = ncConfirmed ? "rgba(220,38,38,0.3)"
            : ncPending ? "rgba(234,88,12,0.35)"
            : "var(--vb-border)";
          return (
          <div key={l.id || `new-${i}`}
            className="p-3 rounded-lg"
            style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
            data-testid={`md-line-row-${i}`}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
              <span className="text-xs uppercase font-semibold opacity-70">
                Line #{i + 1}
              </span>
              {ncConfirmed ? (
                <span className="vb-pill err text-[10px]"
                  data-testid={`md-line-locked-${i}`}>
                  <Lock size={11} /> NC confirmed
                </span>
              ) : ncPending ? (
                <span className="vb-pill warn text-[10px]"
                  data-testid={`md-line-pending-${i}`}>
                  <AlertTriangle size={11} /> NC · pending confirm
                </span>
              ) : (
                <button className="vb-btn ghost" onClick={() => removeLine(i)}
                  style={{ padding: "4px 8px" }}
                  data-testid={`md-line-remove-${i}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <div className="space-y-2">
              <Field label="Batch No.">
                <input className="vb-input" value={l.batch_no}
                  disabled={isLocked}
                  onChange={(e) => setField(i, "batch_no", e.target.value)}
                  data-testid={`md-line-batch-${i}`} />
              </Field>
              <Field label="Item · 产品">
                <input className="vb-input" value={l.item}
                  disabled={isLocked}
                  onChange={(e) => setField(i, "item", e.target.value)}
                  data-testid={`md-line-item-${i}`} />
              </Field>
              <Field label="Quantity">
                <input className="vb-input" value={l.quantity}
                  disabled={isLocked}
                  placeholder="e.g. 50 pcs"
                  onChange={(e) => setField(i, "quantity", e.target.value)}
                  data-testid={`md-line-qty-${i}`} />
              </Field>
              {!isLocked && (
                <label className="flex items-center gap-2 text-sm py-1">
                  <input type="checkbox" checked={!!l.nc_detected}
                    onChange={(e) => setField(i, "nc_detected", e.target.checked)}
                    data-testid={`md-line-nc-${i}`} />
                  Non-conformance detected
                </label>
              )}
              {l.nc_detected && (
                <Field label={ncConfirmed
                  ? `Action taken (confirmed by ${l.nc_confirmation?.confirmed_by_name || "supervisor"})`
                  : ncPending
                    ? "Action taken (awaiting supervisor confirmation)"
                    : "Action taken (required)"}>
                  <textarea className="vb-input" rows={3}
                    value={l.action_taken}
                    disabled={isLocked}
                    placeholder="e.g. Rechecked → still positive → rejected → notified QA"
                    onChange={(e) => setField(i, "action_taken", e.target.value)}
                    data-testid={`md-line-action-${i}`} />
                </Field>
              )}
              {ncPending && canVerify && (
                <button className="vb-btn danger text-xs w-full" type="button"
                  onClick={() => onConfirmNC?.(l.id)}
                  data-testid={`md-line-confirm-nc-${i}`}>
                  <CheckCircle2 size={13} /> Confirm rejection &amp; lock
                </button>
              )}
              <div className="flex items-center gap-2 flex-wrap pt-1">
                {(l.photos || []).map((p, pi) => (
                  <a key={pi} href={p.url} target="_blank" rel="noreferrer"
                    title={fmt(p.uploaded_at)}
                    className="vb-pill text-[10px]">
                    <ImageIcon size={12} /> Photo {pi + 1}
                  </a>
                ))}
                <label className="vb-btn ghost text-xs cursor-pointer"
                  title={l.id ? "Add photo" : "Save first"}>
                  <Camera size={13} /> Add photo
                  <input type="file" accept="image/*" hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadPhoto(l.id, f);
                      e.target.value = "";
                    }}
                    data-testid={`md-line-photo-${i}`} />
                </label>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {err && <div className="vb-pill err mt-3 text-[11px]">{err}</div>}
      <div className="flex justify-between items-center gap-2 mt-3 flex-wrap">
        <button className="vb-btn ghost text-sm" onClick={addLine}
          data-testid="md-line-add">
          <Plus size={14} /> Add line
        </button>
        <button className="vb-btn text-sm" onClick={save} disabled={busy}
          data-testid="md-lines-save">
          {busy ? "Saving…" : "Save lines"}
        </button>
      </div>
    </div>
  );
}

function ProductionLinesReadOnly({ lines, canVerify, recordLocked, onConfirmNC }) {
  if (!lines?.length) return <div className="text-xs opacity-60">No line items.</div>;
  const ncPill = (l) => {
    if (!l.nc_detected) return <span className="opacity-40 text-[10px]">OK</span>;
    if (l.nc_status === "confirmed") {
      return (
        <span className="vb-pill err text-[10px]"
          title={`Confirmed by ${l.nc_confirmation?.confirmed_by_name || "supervisor"}`}>
          <Lock size={10} /> NC confirmed
        </span>
      );
    }
    return (
      <span className="vb-pill warn text-[10px]"
        title="NC awaiting supervisor confirmation">
        <AlertTriangle size={10} /> NC · pending
      </span>
    );
  };
  return (
    <>
      {/* Desktop table */}
      <div className="vb-card p-2 overflow-x-auto hidden md:block">
        <table className="vb-table text-sm" style={{ minWidth: 780 }}>
          <thead>
            <tr>
              <th>Batch</th><th>Item</th><th>Qty</th><th>Status</th>
              <th>Action taken</th><th>Photos</th><th></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const ncPending = l.nc_detected && l.nc_status !== "confirmed";
              return (
                <tr key={l.id || i}
                  data-testid={`md-ro-line-${i}`}
                  style={{ background: ncPending && !recordLocked
                    ? "rgba(234,88,12,0.05)" : undefined }}>
                  <td>{l.batch_no}</td>
                  <td>{l.item}</td>
                  <td>{l.quantity}</td>
                  <td>{ncPill(l)}</td>
                  <td className="text-xs">{l.action_taken || "—"}</td>
                  <td>
                    <div className="flex gap-1">
                      {(l.photos || []).map((p, pi) => (
                        <a key={pi} href={p.url} target="_blank" rel="noreferrer"
                          className="opacity-70 hover:opacity-100">
                          <ImageIcon size={14} />
                        </a>
                      ))}
                    </div>
                  </td>
                  <td>
                    {ncPending && canVerify && !recordLocked && (
                      <button className="vb-btn danger" type="button"
                        style={{ padding: "2px 6px", fontSize: "10px" }}
                        onClick={() => onConfirmNC?.(l.id)}
                        data-testid={`md-ro-confirm-nc-${i}`}>
                        <CheckCircle2 size={11} /> Confirm
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile stacked cards */}
      <div className="md:hidden space-y-2">
        {lines.map((l, i) => {
          const ncPending = l.nc_detected && l.nc_status !== "confirmed";
          return (
          <div key={l.id || i} className="p-3 rounded-lg text-sm"
            style={{ background: ncPending && !recordLocked
              ? "rgba(234,88,12,0.06)" : "var(--vb-elev)",
              border: `1px solid ${ncPending && !recordLocked
                ? "rgba(234,88,12,0.35)" : "var(--vb-border)"}` }}
            data-testid={`md-ro-line-${i}`}>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
              <strong>{l.batch_no || "—"}</strong>
              {ncPill(l)}
            </div>
            <div className="opacity-80">{l.item} · {l.quantity}</div>
            {l.action_taken && (
              <div className="text-xs opacity-70 mt-1">{l.action_taken}</div>
            )}
            {(l.photos || []).length > 0 && (
              <div className="flex gap-1 mt-2">
                {(l.photos || []).map((p, pi) => (
                  <a key={pi} href={p.url} target="_blank" rel="noreferrer"
                    className="vb-pill text-[10px]">
                    <ImageIcon size={12} /> Photo {pi + 1}
                  </a>
                ))}
              </div>
            )}
            {ncPending && canVerify && !recordLocked && (
              <button className="vb-btn danger text-xs w-full mt-2" type="button"
                onClick={() => onConfirmNC?.(l.id)}
                data-testid={`md-ro-confirm-nc-${i}`}>
                <CheckCircle2 size={13} /> Confirm rejection &amp; lock
              </button>
            )}
          </div>
          );
        })}
      </div>
    </>
  );
}
